const ENVIRONMENTS = [
  { id: "beta", label: "Beta" },
  { id: "alpha", label: "Alpha" },
  { id: "production", label: "Production" },
];

/**
 * Turns a platform-health API payload into the board snapshot.
 * @param {object} health
 * @param {string} [environmentId]
 * @returns {object}
 */
export function mapLiveHealth(health, environmentId = "production") {
  const selected =
    ENVIRONMENTS.find((environment) => environment.id === environmentId) ||
    ENVIRONMENTS[2];
  const services = Array.isArray(health?.services) ? health.services : [];
  const cards = services.map((service) =>
    mapServiceCard(service, selected.id)
  );
  const apiService = services.find((service) => service.id === "api");
  const productionApi = apiService?.stages?.production;
  const selectedApi = apiService?.stages?.[selected.id];

  return {
    title: "App health & releases",
    dataBadge: "Live",
    subtitle: `${selected.label} overview · ${cards.length} services · live health checks`,
    environment: selected.label,
    environmentId: selected.id,
    environments: ENVIRONMENTS,
    region: "All regions",
    capturedAt: formatCapturedAt(health?.capturedAt),
    serviceSummary: summarizeCards(cards),
    freshness: "Live health checks",
    services: cards,
    release: mapRelease(apiService, health?.checks, selectedApi, selected.id),
    incident: mapIncident(productionApi),
    safeguards: mapSafeguards(services, health?.checks, selected.id),
    footer: {
      notice: health?.checks?.connected
        ? "Live health checks and GitHub deploy workflows. Crash-free sessions and SLOs are not connected yet."
        : "Live health checks for the API and dispatch app. Crash-free sessions, SLOs, and GitHub checks are not connected yet.",
      window: "Health: latest check · SLO: not connected",
    },
  };
}

/**
 * @param {object} service
 * @param {string} environmentId
 */
function mapServiceCard(service, environmentId) {
  const probe = service?.stages?.[environmentId] || {};

  if (probe.status === "unknown" && probe.workflow) {
    return cardFromWorkflow(service, probe.workflow);
  }

  const status = probe.status || "unknown";
  const known = status === "healthy" || status === "degraded" || status === "down";

  return {
    id: service.id,
    name: service.name,
    platform: service.platform,
    icon: service.icon,
    status,
    statusLabel: statusLabel(status),
    metric: known && probe.latencyMs != null ? `${probe.latencyMs} ms` : "—",
    metricLabel: service.metricLabel || "Health check",
    detail: detailForProbe(probe),
    detailTone: status === "down" || status === "degraded" ? "warning" : undefined,
    sloLabel: "Health check",
    sloValue: known
      ? `${probe.httpStatus || "—"} · ${probe.latencyMs ?? "—"} ms`
      : "Not connected",
    version: versionLabel(probe),
    updated: known ? "just now" : "—",
    intervals: [],
  };
}

/**
 * @param {object} service
 * @param {object} workflow
 */
function cardFromWorkflow(service, workflow) {
  const passed = workflow.conclusion === "success";
  const failed = workflow.conclusion === "failure";
  const cancelled = workflow.conclusion === "cancelled";
  const status = passed ? "healthy" : failed || cancelled ? "down" : "unknown";

  return {
    id: service.id,
    name: service.name,
    platform: service.platform,
    icon: service.icon,
    status,
    statusLabel: passed
      ? "Passed"
      : failed
        ? "Failed"
        : cancelled
          ? "Cancelled"
          : "Running",
    metric: passed ? "Passed" : failed ? "Failed" : cancelled ? "Cancelled" : "Running",
    metricLabel: "Latest workflow",
    detail: workflow.title || "Deploy workflow",
    detailTone: passed ? undefined : "warning",
    sloLabel: "Workflow",
    sloValue: workflow.sha || "No sha",
    version: workflow.sha || "Not connected",
    updated: "latest run",
    intervals: [],
  };
}

/**
 * @param {object | undefined} apiService
 * @param {object | undefined} checks
 * @param {object | undefined} selectedApi
 * @param {string} environmentId
 */
function mapRelease(apiService, checks, selectedApi, environmentId) {
  const production = apiService?.stages?.production;
  const apiRun = checks?.repos
    ?.find((repo) => repo.id === "api")
    ?.stages?.[environmentId];
  const healthy = production?.status === "healthy";
  const measured =
    production?.status === "healthy" ||
    production?.status === "degraded" ||
    production?.status === "down";

  return {
    statusLabel: !measured
      ? "Not connected"
      : healthy
        ? "Live"
        : "Needs attention",
    statusTone: !measured ? "neutral" : healthy ? "healthy" : "warning",
    statusIcon: !measured ? "info" : healthy ? "checkBadge" : "pauseBadge",
    repository: "ferda-tech/attunelogic-api",
    version: production?.version ? `v${production.version}` : "version unknown",
    ref: apiRun?.sha
      ? `· ${environmentId} · ${apiRun.sha}`
      : "· beta · alpha · production",
    linkLabel: apiRun?.url ? "View GitHub run" : "View health check",
    linkHref: apiRun?.url || selectedApi?.url || production?.url || undefined,
    timing: production?.latencyMs != null ? `Production ${production.latencyMs} ms` : "Production not measured",
    stages: [
      mapChecksStage(checks, environmentId),
      stageFromProbe("Beta", apiService?.stages?.beta),
      stageFromProbe("Alpha", apiService?.stages?.alpha),
      stageFromProbe("Production", production),
    ],
    rolloutLabel: "Production",
    rolloutValue: healthy
      ? "Responding"
      : measured
        ? "Not responding"
        : "Not measured",
    rolloutPercent: healthy ? 100 : production?.status === "degraded" ? 50 : 0,
    attention: !healthy,
    guardrailTitle: !measured
      ? "Production health check has not been measured"
      : healthy
        ? "Production health check passed"
        : "Production health check did not pass",
    guardrailDetail: production
      ? `${versionLabel(production)} · ${production.latencyMs ?? "—"} ms`
      : "No production reading yet.",
    rollbackLabel: "Rollback not connected",
    rollbackTone: "neutral",
    rollbackIcon: "info",
    previousStable: "Previous stable version is not connected",
  };
}

/**
 * @param {object | undefined} checks
 * @param {string} environmentId
 */
function mapChecksStage(checks, environmentId) {
  const repos = checks?.repos || [];
  const runs = repos.map((repo) => repo.stages?.[environmentId] || null);
  const present = runs.filter(Boolean);

  if (!checks?.connected || present.length === 0) {
    return {
      id: "checks",
      title: "Checks & build",
      detail: checks?.detail || "GitHub checks are not connected",
      timing: "No workflow data",
      status: "unknown",
    };
  }

  const passed = present.filter((run) => run.conclusion === "success").length;
  const complete = passed === repos.length;
  const label = environmentId.charAt(0).toUpperCase() + environmentId.slice(1);

  return {
    id: "checks",
    title: "Checks & build",
    detail: `${label} · ${passed}/${repos.length} workflows passed`,
    timing: present[0]?.sha || "Latest deploy workflow",
    status: complete ? "complete" : "paused",
  };
}

/**
 * @param {string} title
 * @param {object | undefined} probe
 */
function stageFromProbe(title, probe) {
  const status = probe?.status;

  if (status === "healthy") {
    return {
      id: title.toLowerCase(),
      title,
      detail: probe.version ? `v${probe.version}` : "Health check passed",
      timing: `${probe.latencyMs} ms · Responding`,
      status: "complete",
    };
  }

  if (status === "degraded" || status === "down") {
    return {
      id: title.toLowerCase(),
      title,
      detail: status === "degraded" ? "Slow response" : "Health check failed",
      timing:
        probe.error === "timeout"
          ? "Timed out"
          : `${probe.httpStatus || "No response"}`,
      status: "paused",
    };
  }

  return {
    id: title.toLowerCase(),
    title,
    detail: probe?.detail || "Not connected",
    timing: "No health check",
    status: "unknown",
  };
}

/**
 * @param {object | undefined} productionApi
 */
function mapIncident(productionApi) {
  const status = productionApi?.status;
  const productionDown = status === "down" || status === "degraded";

  if (!productionDown) {
    return {
      open: false,
      countLabel: "None",
      linkLabel: "",
      severity: "",
      id: "",
      state: status === "healthy" ? "Clear" : "Not measured",
      title: "No active incident",
      summary:
        status === "healthy"
          ? "The production API health check is responding."
          : "Production API health has not been measured.",
      owner: "",
      ownerInitials: "",
      updated: "Latest check",
    };
  }

  return {
    open: true,
    countLabel: "1 open",
    linkLabel: productionApi?.url ? "Health check" : "",
    linkHref: productionApi?.url || undefined,
    severity: "Health",
    id: "API",
    state: productionApi?.status === "degraded" ? "Degraded" : "Down",
    title: "Production API health check failed",
    summary:
      productionApi?.error === "timeout"
        ? "The production health check timed out."
        : "The production health check did not return a healthy response.",
    owner: "Source: public health check",
    ownerInitials: "",
    updated: "Latest check",
  };
}

/**
 * @param {object[]} services
 * @param {object | undefined} githubChecks
 * @param {string} environmentId
 */
function mapSafeguards(services, githubChecks, environmentId) {
  const checks = services.map((service) => {
    const probe = service?.stages?.production;
    const status = probe?.status;
    const holding = status !== "healthy";

    return {
      id: service.id,
      label: `${service.name} production`,
      value: statusLabel(status),
      status: holding ? "holding" : "passed",
    };
  });

  (githubChecks?.repos || []).forEach((repo) => {
    const run = repo.stages?.[environmentId];
    const passed = run?.conclusion === "success";

    checks.push({
      id: `workflow-${repo.id}`,
      label: `${repo.name} workflow`,
      value: workflowValue(run),
      status: passed ? "passed" : "holding",
    });
  });
  const holdingCount = checks.filter((check) => check.status === "holding").length;
  const passedCount = checks.length - holdingCount;

  return {
    summary: `${passedCount} passed · ${holdingCount} holding`,
    checks,
    owner: "Release owner is not connected",
  };
}

/**
 * @param {object[]} cards
 */
function summarizeCards(cards) {
  const healthy = cards.filter((card) => card.status === "healthy").length;
  const attention = cards.filter(
    (card) => card.status === "degraded" || card.status === "down"
  ).length;
  const unknown = cards.length - healthy - attention;
  const parts = [`${healthy} healthy`];

  if (attention) {
    parts.push(`${attention} need attention`);
  }
  if (unknown) {
    parts.push(`${unknown} not connected`);
  }

  return parts.join(" · ");
}

/**
 * @param {object | null | undefined} run
 */
function workflowValue(run) {
  if (!run) return "No run";
  if (run.conclusion === "success") return "Passed";
  if (run.conclusion === "failure") return "Failed";
  if (run.conclusion === "cancelled") return "Cancelled";
  if (run.status === "in_progress" || run.status === "queued") return "Running";
  return "Not finished";
}

/**
 * @param {string | undefined} status
 */
function statusLabel(status) {
  if (status === "healthy") return "Healthy";
  if (status === "degraded") return "Degraded";
  if (status === "down") return "Down";
  return "Not connected";
}

/**
 * @param {object} probe
 */
function detailForProbe(probe) {
  if (!probe || probe.status === "unknown") {
    return probe?.detail || "Not connected";
  }
  if (probe.error === "timeout") {
    return "Health check timed out";
  }
  if (probe.status === "down") {
    return `Health check failed${probe.httpStatus ? ` · ${probe.httpStatus}` : ""}`;
  }
  if (probe.status === "degraded") {
    return `Slow response · ${probe.latencyMs} ms`;
  }
  return `Responding · ${probe.latencyMs} ms`;
}

/**
 * @param {object} probe
 */
function versionLabel(probe) {
  if (probe?.version) {
    return `v${probe.version}`;
  }
  if (probe?.status === "unknown") {
    return "Not connected";
  }
  return "Version not reported";
}

/**
 * @param {string | undefined} iso
 */
function formatCapturedAt(iso) {
  const date = iso ? new Date(iso) : new Date();
  if (Number.isNaN(date.getTime())) {
    return "Snapshot: just now";
  }

  const formatted = new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "UTC",
    hourCycle: "h23",
  }).format(date);

  return `Snapshot: ${formatted} UTC`;
}
